// src/pages/UniversalPage.tsx

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useContext,
} from 'react';
import styled from 'styled-components';

import {
  HEADER_HEIGHT,
  SEPARATOR_HEIGHT,
  MEDIA_BREAKPOINTS,
} from '@/constants/layout';

import { ThemeContext } from '@/theme/ThemeProvider';
import {
  getBackgroundImagesForEvent,
  getBackgroundImageSrcSetForEvent,
} from '@/constants/images';

import { type PageOverrides, type SeoService } from '@/constants/core';
import type { EventType, Region } from '@/constants/geo';

import type { InternalLink } from '@/utils/internalLinks';
import type { MenuDesktopProps } from '@/components/MenuDesktop/MenuDesktop';
import type { MenuMobileProps } from '@/components/MenuMobile/MenuMobile';

import Section from '@/components/Section/Section';

import Separator from '@/components/StyledComponents/Separator';
import HeroSection from '@/Sections/HeroSection/HeroSection';

import { useI18n } from '@/i18n/LanguageProvider';
import { scheduleSpaReadyAfterPaint } from '@/utils/spaReady';

const mediumScreenBreakpoint = parseInt(MEDIA_BREAKPOINTS.mediumScreen, 10);
const HERO_IMAGE_ALT = 'די ג׳יי אליאור מור - תמונת פתיחה';

// ---------- styled ----------

const SectionsWrapper = styled.div`
  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding-top: ${HEADER_HEIGHT}px;
    background-color: #000;
  }
`;

const SectionFallback = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
`;

const FooterMount = styled.div`
  min-height: 1px;
  background: #000;
`;

// ---------- lazy sections ----------

const importStorySection = () => import('@/Sections/StorySection/StorySection');
const importGallerySection = () =>
  import('@/Sections/GallerySection/GallerySection');
const importContactSection = () =>
  import('@/Sections/ContactSection/ContactSection');
const importInfoSection = () => import('@/Sections/InfoSection/InfoSection');
const importReviewsSection = () =>
  import('@/Sections/ReviewsSection/ReviewsSection');
const importEventPlanningSection = () =>
  import('@/Sections/EventPlanningSection/EventPlanningSection');
const importFooter = () => import('@/components/Footer/Footer');

const StorySection = lazy(importStorySection);
const GallerySection = lazy(importGallerySection);
const ContactSection = lazy(importContactSection);
const InfoSection = lazy(importInfoSection);
const ReviewsSection = lazy(importReviewsSection);
const EventPlanningSection = lazy(importEventPlanningSection);
const Footer = lazy(importFooter);
const MenuDesktop = lazy(
  () => import('@/components/MenuDesktop/MenuDesktop'),
) as React.LazyExoticComponent<React.ComponentType<MenuDesktopProps>>;
const MenuMobile = lazy(
  () => import('@/components/MenuMobile/MenuMobile'),
) as React.LazyExoticComponent<React.ComponentType<MenuMobileProps<SectionId>>>;

const sectionImporters: Partial<Record<SectionId, () => Promise<unknown>>> = {
  story: importStorySection,
  gallery: importGallerySection,
  contact: importContactSection,
  info: importInfoSection,
  reviews: importReviewsSection,
  planning: importEventPlanningSection,
};

// ---------- types ----------

export type SectionId =
  | 'home'
  | 'story'
  | 'gallery'
  | 'contact'
  | 'info'
  | 'reviews'
  | 'planning';

type Props = {
  overrides?: PageOverrides;
  internalLinks?: {
    section2?: InternalLink[];
    section4?: InternalLink[];
    footer?: InternalLink[];
  };
  serviceForSection5?: SeoService;
  region?: Region;
  eventType?: EventType;
};

function SpaReadyBridge() {
  useLayoutEffect(() => {
    return scheduleSpaReadyAfterPaint();
  }, []);

  return null;
}

const POST_LCP_FALLBACK_DELAY_MS = 4200;
const POST_LCP_OBSERVED_DELAY_MS = 800;
const POST_LCP_SECTION_WARM_INTERVAL_MS = 450;
const POST_LCP_SECTION_WARM_IDLE_TIMEOUT_MS = 1800;
const INITIAL_SCROLL_MEASURE_IDLE_TIMEOUT_MS = 2600;
const INITIAL_SCROLL_MEASURE_FALLBACK_MS = 1400;
const MOBILE_SECTION_PRELOAD_MARGIN = '900px 0px';
const DESKTOP_SECTION_PRELOAD_MARGIN = '1600px 0px';

// ==========================================================
// COMPONENT
// ==========================================================

const UniversalPage: React.FC<Props> = ({
  overrides,
  internalLinks,
  region,
  eventType = 'אירוע',
}) => {
  const { i18n } = useI18n();

  const allSections = i18n.MENU_SECTIONS as readonly {
    id: SectionId;
    label: string;
  }[];

  const menuSections = useMemo(
    () =>
      allSections.map(section =>
        section.id === 'planning' && overrides?.section7?.title
          ? { ...section, label: overrides.section7.title }
          : section,
      ),
    [allSections, overrides?.section7?.title],
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loadedSectionIds, setLoadedSectionIds] = useState<Set<SectionId>>(
    () => new Set<SectionId>(['home']),
  );
  const [shouldLoadFooter, setShouldLoadFooter] = useState(false);
  const [isHeroReady, setIsHeroReady] = useState(false);
  const [shouldPreloadNearbySections, setShouldPreloadNearbySections] =
    useState(false);

  const [selectedSection, setSelectedSection] = useState<SectionId>(
    menuSections[0].id,
  );
  const selectedSectionRef = useRef<SectionId>(menuSections[0].id);

  const sectionRefs = useRef<Map<SectionId, HTMLElement>>(new Map());
  const sectionBoundsRef = useRef<
    Map<SectionId, { top: number; bottom: number }>
  >(new Map());
  const footerRef = useRef<HTMLDivElement | null>(null);
  const preloadedBackgroundsRef = useRef<Set<string>>(new Set());

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  // ---------- theme / backgrounds ----------

  const { mode, isDesktop, setEventType } = useContext(ThemeContext);

  useEffect(() => {
    setEventType(eventType);
  }, [eventType, setEventType]);

  const markSectionLoaded = useCallback((sectionId: SectionId) => {
    setLoadedSectionIds(prev => {
      if (prev.has(sectionId)) return prev;
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
  }, []);

  const backgroundImageSize = isDesktop ? 'desktop' : 'mobile';

  const bgArr = useMemo(() => {
    return (
      getBackgroundImagesForEvent(eventType)[mode]?.[backgroundImageSize] ?? []
    );
  }, [backgroundImageSize, eventType, mode]);

  const getBgForSection = useCallback(
    (id: SectionId) => {
      const index = allSections.findIndex(s => s.id === id);
      return bgArr[index] ?? '';
    },
    [bgArr, allSections],
  );

  // ---------- content checks ----------

  const hasSection5Content = useMemo(() => {
    const o = overrides?.section5;
    return Boolean(
      o?.title ||
        o?.subtitle ||
        o?.h3Headings?.length ||
        o?.faqs?.length ||
        o?.links?.length,
    );
  }, [overrides?.section5]);

  const hasSection6Content = Boolean(overrides?.section6?.reviews?.length);

  const hasSection7Content = Boolean(overrides?.section7?.items?.length);

  // ---------- visible sections ----------

  const visibleSections = useMemo(() => {
    let arr = [...menuSections];

    if (!hasSection5Content) arr = arr.filter(s => s.id !== 'info');

    if (!hasSection6Content) arr = arr.filter(s => s.id !== 'reviews');

    if (!hasSection7Content) arr = arr.filter(s => s.id !== 'planning');

    return arr;
  }, [
    menuSections,
    hasSection5Content,
    hasSection6Content,
    hasSection7Content,
  ]);

  useEffect(() => {
    setLoadedSectionIds(prev => {
      const visibleIds = new Set(visibleSections.map(section => section.id));
      const next = new Set<SectionId>(['home']);

      prev.forEach(sectionId => {
        if (visibleIds.has(sectionId)) next.add(sectionId);
      });

      if (
        next.size === prev.size &&
        Array.from(next).every(sectionId => prev.has(sectionId))
      ) {
        return prev;
      }

      return next;
    });
  }, [visibleSections]);

  useEffect(() => {
    setIsHeroReady(false);
    setShouldPreloadNearbySections(false);
    setShouldLoadFooter(false);
  }, [eventType, mode, isDesktop]);

  useEffect(() => {
    if (!isHeroReady) return;

    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    let lcpObserver: PerformanceObserver | null = null;
    let didStartPostLcpWork = false;
    const timeoutIds: number[] = [];
    const idleIds: number[] = [];
    let didCancel = false;

    const scheduleTimeout = (callback: () => void, delay: number) => {
      const timeoutId = window.setTimeout(callback, delay);
      timeoutIds.push(timeoutId);
      return timeoutId;
    };

    const scheduleIdle = (callback: () => void) => {
      if (typeof window.requestIdleCallback === 'function') {
        const idleId = window.requestIdleCallback(callback, {
          timeout: POST_LCP_SECTION_WARM_IDLE_TIMEOUT_MS,
        });
        idleIds.push(idleId);
        return;
      }

      scheduleTimeout(callback, POST_LCP_SECTION_WARM_INTERVAL_MS);
    };

    const preloadSectionBackground = (sectionId: SectionId) => {
      const url = getBgForSection(sectionId);
      if (!url || preloadedBackgroundsRef.current.has(url)) return;

      preloadedBackgroundsRef.current.add(url);
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
    };

    const warmRemainingSections = () => {
      visibleSections
        .filter(section => section.id !== 'home')
        .forEach((section, index) => {
          scheduleTimeout(() => {
            scheduleIdle(() => {
              if (didCancel) return;

              preloadSectionBackground(section.id);
              void sectionImporters[section.id]?.().catch(() => undefined);
            });
          }, index * POST_LCP_SECTION_WARM_INTERVAL_MS);
        });
    };

    const startPostLcpWork = () => {
      if (didStartPostLcpWork) return;
      didStartPostLcpWork = true;

      setShouldPreloadNearbySections(true);
      warmRemainingSections();
    };

    const schedulePostLcpWork = () => {
      const fallbackTimeout = scheduleTimeout(
        startPostLcpWork,
        POST_LCP_FALLBACK_DELAY_MS,
      );

      if (typeof PerformanceObserver === 'undefined') return;

      try {
        lcpObserver = new PerformanceObserver(list => {
          const sawHeroLcp = list.getEntries().some(entry => {
            const element = (
              entry as PerformanceEntry & {
                element?: Element | null;
              }
            ).element;

            return (
              element instanceof HTMLImageElement &&
              element.alt === HERO_IMAGE_ALT
            );
          });

          if (!sawHeroLcp) return;

          window.clearTimeout(fallbackTimeout);
          lcpObserver?.disconnect();
          scheduleTimeout(startPostLcpWork, POST_LCP_OBSERVED_DELAY_MS);
        });

        (
          lcpObserver as PerformanceObserver & {
            observe(options: { type: string; buffered: boolean }): void;
          }
        ).observe({ type: 'largest-contentful-paint', buffered: true });
      } catch {
        lcpObserver = null;
      }
    };

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        schedulePostLcpWork();
      });
    });

    return () => {
      didCancel = true;
      if (firstFrame != null) window.cancelAnimationFrame(firstFrame);
      if (secondFrame != null) window.cancelAnimationFrame(secondFrame);
      lcpObserver?.disconnect();

      timeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
      if (typeof window.cancelIdleCallback === 'function') {
        idleIds.forEach(idleId => window.cancelIdleCallback(idleId));
      }
    };
  }, [getBgForSection, isHeroReady, visibleSections]);

  // keep selected valid

  useEffect(() => {
    selectedSectionRef.current = selectedSection;
  }, [selectedSection]);

  useEffect(() => {
    if (!visibleSections.some(s => s.id === selectedSection)) {
      const nextSection = visibleSections[0].id;
      selectedSectionRef.current = nextSection;
      setSelectedSection(nextSection);
    }
  }, [visibleSections, selectedSection]);

  // ---------- scrollToSection ----------

  const scrollToSection = useCallback(
    (sectionId: SectionId) => {
      const el = sectionRefs.current.get(sectionId);

      if (!el) return;

      markSectionLoaded(sectionId);

      const top = el.getBoundingClientRect().top + window.pageYOffset;

      const isDesktopNow = window.innerWidth >= mediumScreenBreakpoint;

      const y =
        top -
        (isDesktopNow
          ? HEADER_HEIGHT + SEPARATOR_HEIGHT.largeScreen
          : SEPARATOR_HEIGHT.smallScreen);

      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });

      selectedSectionRef.current = sectionId;
      setSelectedSection(sectionId);
    },
    [markSectionLoaded],
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      visibleSections.forEach(section => markSectionLoaded(section.id));
      setShouldLoadFooter(true);
      return;
    }

    const sectionLoadRootMargin = shouldPreloadNearbySections
      ? isDesktop
        ? DESKTOP_SECTION_PRELOAD_MARGIN
        : MOBILE_SECTION_PRELOAD_MARGIN
      : '0px';

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const sectionId = (entry.target as HTMLElement).dataset.sectionId as
            | SectionId
            | undefined;

          if (!sectionId) return;
          markSectionLoaded(sectionId);
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: sectionLoadRootMargin,
        threshold: 0.01,
      },
    );

    visibleSections.forEach(section => {
      if (loadedSectionIds.has(section.id)) return;
      const el = sectionRefs.current.get(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [
    loadedSectionIds,
    markSectionLoaded,
    shouldPreloadNearbySections,
    isDesktop,
    visibleSections,
  ]);

  useEffect(() => {
    if (shouldLoadFooter) return;

    const el = footerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldLoadFooter(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadFooter(true);
        observer.disconnect();
      },
      {
        rootMargin: '1000px 0px',
        threshold: 0,
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoadFooter, visibleSections.length]);

  // ---------- scroll spy ----------

  useEffect(() => {
    if (!isHeroReady) return;

    let raf: number | null = null;
    let measureRaf: number | null = null;
    let initialFrame: number | null = null;
    let postPaintFrame: number | null = null;
    let initialMeasureIdle: number | null = null;
    let initialMeasureTimeout: number | null = null;

    const updateSelectedSection = () => {
      raf = null;

      const probeY = window.scrollY + window.innerHeight / 2;

      let current = selectedSectionRef.current;

      for (const s of visibleSections) {
        const bounds = sectionBoundsRef.current.get(s.id);
        if (!bounds) continue;

        if (probeY >= bounds.top && probeY < bounds.bottom) {
          current = s.id;
          break;
        }
      }

      if (current !== selectedSectionRef.current) {
        selectedSectionRef.current = current;
        setSelectedSection(current);
      }
    };

    const scheduleSelectedUpdate = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(updateSelectedSection);
    };

    const measureSections = () => {
      measureRaf = null;
      const next = new Map<SectionId, { top: number; bottom: number }>();

      for (const s of visibleSections) {
        const el = sectionRefs.current.get(s.id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        next.set(s.id, {
          top,
          bottom: top + rect.height,
        });
      }

      sectionBoundsRef.current = next;
      scheduleSelectedUpdate();
    };

    const scheduleMeasure = () => {
      if (measureRaf != null) return;
      measureRaf = window.requestAnimationFrame(measureSections);
    };

    const handleScroll = () => {
      if (!sectionBoundsRef.current.size) {
        scheduleMeasure();
      }

      scheduleSelectedUpdate();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('orientationchange', scheduleMeasure);

    const scheduleInitialMeasure = () => {
      initialFrame = window.requestAnimationFrame(() => {
        postPaintFrame = window.requestAnimationFrame(() => {
          scheduleMeasure();
        });
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      initialMeasureIdle = window.requestIdleCallback(scheduleInitialMeasure, {
        timeout: INITIAL_SCROLL_MEASURE_IDLE_TIMEOUT_MS,
      });
    } else {
      initialMeasureTimeout = window.setTimeout(
        scheduleInitialMeasure,
        INITIAL_SCROLL_MEASURE_FALLBACK_MS,
      );
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('orientationchange', scheduleMeasure);

      if (raf != null) {
        window.cancelAnimationFrame(raf);
      }
      if (measureRaf != null) {
        window.cancelAnimationFrame(measureRaf);
      }
      if (initialFrame != null) {
        window.cancelAnimationFrame(initialFrame);
      }
      if (postPaintFrame != null) {
        window.cancelAnimationFrame(postPaintFrame);
      }
      if (
        initialMeasureIdle != null &&
        typeof window.cancelIdleCallback === 'function'
      ) {
        window.cancelIdleCallback(initialMeasureIdle);
      }
      if (initialMeasureTimeout != null) {
        window.clearTimeout(initialMeasureTimeout);
      }
    };
  }, [isHeroReady, visibleSections]);

  // ---------- sections render ----------

  const sections = useMemo(
    () =>
      visibleSections.map((s, i) => {
        const id = s.id;

        const comp =
          id === 'home' ? (
            <HeroSection
              overrides={overrides?.section1}
              secondaryMediaReady={isHeroReady}
            />
          ) : id === 'story' ? (
            <StorySection
              overrides={overrides?.section2}
              extraLinks={internalLinks?.section2}
            />
          ) : id === 'gallery' ? (
            <GallerySection overrides={overrides?.section3} />
          ) : id === 'contact' ? (
            <ContactSection
              overrides={overrides?.section4}
              extraLinks={internalLinks?.section4}
              region={region}
            />
          ) : id === 'info' ? (
            <InfoSection overrides={overrides?.section5} />
          ) : id === 'reviews' ? (
            <ReviewsSection overrides={overrides?.section6} />
          ) : (
            <EventPlanningSection overrides={overrides?.section7} />
          );

        const bgUrl = getBgForSection(id);
        const heroImageSrcSet =
          i === 0
            ? getBackgroundImageSrcSetForEvent(
                eventType,
                mode,
                backgroundImageSize,
                i,
              )
            : undefined;
        const shouldLoadSection = loadedSectionIds.has(id);

        return (
          <React.Fragment key={id}>
            {i === 0 && <Separator />}

            <Section
              ref={el => {
                if (el) {
                  (el as HTMLElement).dataset.sectionId = id;
                  sectionRefs.current.set(id, el as HTMLElement);
                } else {
                  sectionRefs.current.delete(id);
                }
              }}
              sectionIndex={i}
              backgroundImageUrl={bgUrl}
              heroImageSrcSet={heroImageSrcSet}
              shouldLoad={shouldLoadSection}
              onHeroReady={i === 0 ? () => setIsHeroReady(true) : undefined}
            >
              <Suspense fallback={<SectionFallback />}>{comp}</Suspense>
            </Section>

            <Separator $reverse={i % 2 === 0} />
          </React.Fragment>
        );
      }),
    [
      visibleSections,
      overrides,
      internalLinks,
      region,
      getBgForSection,
      backgroundImageSize,
      eventType,
      mode,
      loadedSectionIds,
      isHeroReady,
    ],
  );

  // ---------- SPA ready ----------

  // ==========================================================

  return (
    <>
      <SpaReadyBridge />

      <Suspense fallback={null}>
        {isDesktop ? (
          <MenuDesktop
            sections={visibleSections}
            selectedSection={selectedSection}
            setSelectedSection={setSelectedSection}
            scrollToSection={scrollToSection}
          />
        ) : (
          <MenuMobile
            sections={visibleSections}
            isMenuOpen={isMenuOpen}
            toggleMenu={toggleMenu}
            selectedSection={selectedSection}
            scrollToSection={scrollToSection}
          />
        )}
      </Suspense>

      <SectionsWrapper>{sections}</SectionsWrapper>

      <FooterMount ref={footerRef}>
        {shouldLoadFooter && (
          <Suspense fallback={null}>
            <Footer footerLinks={internalLinks?.footer} region={region} />
          </Suspense>
        )}
      </FooterMount>
    </>
  );
};

export default UniversalPage;
